import {
  ColumnDef,
  createColumnHelper,
  type Table,
} from '@tanstack/react-table'
import { Checkbox } from '../../../Checkbox'
import type { ColumnHeader, RowDisabledTooltip } from '../../types'
import Hourglass03 from '@sylveraio/untitled-ui-icons-react/build/esm/Hourglass03'
import Lock01 from '@sylveraio/untitled-ui-icons-react/build/esm/Lock01'
import { Tooltip } from '../../../Tooltip'
import { RadioButton } from '../../../RadioButton'

export function getColumns(
  columnDefinitions: Array<ColumnHeader>,
  canSelect?: 'all' | 'single',
  selectedRowCount?: number,
  rowSelectionLimit?: number,
  rowDisabledTooltip?: RowDisabledTooltip,
): ColumnDef<Record<string, unknown>, any>[] {
  const columnHelper = createColumnHelper<Record<string, unknown>>()
  if (!columnDefinitions) return []

  const { title, supportingText, placement, subtle } = rowDisabledTooltip || {}

  const getSelectableRows = (table: Table<Record<string, unknown>>) =>
    table.getRowModel().rows.filter(
      //@ts-ignore
      (row) => !row.original._isComingSoon && !row.original._locked,
    )

  const areAllSelectableRowsSelected = (
    table: Table<Record<string, unknown>>,
  ) => {
    const selectableRows = getSelectableRows(table)
    return selectableRows.every((row) => row.getIsSelected())
  }

  const handleOnChangeRowCheckboxes = (
    table: Table<Record<string, unknown>>,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (
      typeof rowSelectionLimit !== 'undefined' &&
      typeof selectedRowCount !== 'undefined'
    ) {
      if (selectedRowCount > 0) {
        table.toggleAllRowsSelected(false)
      } else {
        const rowsToSelect = getSelectableRows(table).slice(
          0,
          rowSelectionLimit,
        )
        const updatedSelection = rowsToSelect.reduce<Record<string, boolean>>(
          (acc, row) => {
            acc[row.id] = true
            return acc
          },
          {},
        )
        table.setRowSelection(updatedSelection)
      }
    } else {
      const rowsToSelect = getSelectableRows(table)
      const updatedSelection = event.target.checked
        ? rowsToSelect.reduce<Record<string, boolean>>((acc, row) => {
            acc[row.id] = true
            return acc
          }, {})
        : {}

      table.setRowSelection(updatedSelection)
    }
  }

  const columns: Array<ColumnDef<Record<string, unknown>, any>> =
    columnDefinitions.map((col) =>
      columnHelper.accessor(col.id, {
        id: col.id,
        header: col.text,
        enableSorting: col.isSortable || false,
        maxSize: col?.width,
        minSize: col?.width,
        meta: {
          justify: col.justify,
          showOnRowHover: col.showOnHover,
          transparentBackground: col.transparentBackground,
        },
      }),
    )

  const isDisabled = (row: any) =>
    typeof rowSelectionLimit !== 'undefined' &&
    typeof selectedRowCount !== 'undefined'
      ? selectedRowCount >= rowSelectionLimit && !row.getIsSelected()
      : !row.getCanSelect()

  const renderCheckboxOrIcon = (row: any) => {
    if (row.original._isComingSoon) {
      return <Hourglass03 className="w-5 h-5 stroke-icon-subtle" />
    }
    if (row.original._locked) {
      return <Lock01 className="w-5 h-5 stroke-icon-subtle" />
    }
    if (isDisabled(row) && rowDisabledTooltip) {
      return (
        <Tooltip
          title={title}
          supportingText={supportingText}
          placement={placement}
          subtle={subtle}
        >
          <Checkbox
            testId="table-checkbox"
            state={row.getIsSelected() ? 'selected' : 'unselected'}
            onChange={(e) => {
              row.getToggleSelectedHandler()(e)
            }}
            onClick={(e) => {
              e.stopPropagation()
            }}
            disabled
            name={`select-all-${row.id}`}
            id={`select-all-${row.id}`}
            label=""
          />
        </Tooltip>
      )
    }
    return (
      <Checkbox
        testId="table-checkbox"
        state={row.getIsSelected() ? 'selected' : 'unselected'}
        onChange={(e) => {
          row.getToggleSelectedHandler()(e)
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
        disabled={isDisabled(row)}
        name={`select-all-${row.id}`}
        id={`select-all-${row.id}`}
        label=""
      />
    )
  }

  const renderRadioButton = (
    row: any,
    table: Table<Record<string, unknown>>,
  ) => {
    return (
      <RadioButton
        testId="table-radio-button"
        value={row.id}
        checked={row.getIsSelected()}
        onChange={() => {
          table.setRowSelection({ [row.id]: true })
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
        name={`select-single-${row.id}`}
        id={`select-single-${row.id}`}
        label=""
      />
    )
  }

  if (canSelect === 'all') {
    columns.unshift(
      columnHelper.accessor('select-all', {
        id: 'select-all',
        header: ({ table, header }) => (
          <th key={header.id} style={{ width: 40 }}>
            <div className="text-left p-4 pr-0 flex gap-x-1 items-center !w-min">
              <Checkbox
                state={
                  areAllSelectableRowsSelected(table)
                    ? 'selected'
                    : 'unselected'
                }
                onChange={(event) => handleOnChangeRowCheckboxes(table, event)}
                name="select-all"
                id="select-all"
                label=""
              />
            </div>
          </th>
        ),
        cell: ({ row }) => (
          <div className="text-left pr-0 flex gap-x-1 items-center justify-center align-center">
            {renderCheckboxOrIcon(row)}
          </div>
        ),
        size: 40,
      }) as ColumnDef<Record<string, unknown>, any>,
    )
  } else if (canSelect === 'single') {
    columns.unshift(
      columnHelper.accessor('select-single', {
        id: 'select-single',
        header: ({ header }) => <th key={header.id} style={{ width: 40 }} />,
        cell: ({ row, table }) => (
          <div className="text-left pr-0 flex gap-x-1 items-center justify-center align-center">
            {renderRadioButton(row, table)}
          </div>
        ),
        size: 40,
      }) as ColumnDef<Record<string, unknown>, any>,
    )
  }
  return columns
}
